import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import "../../Styles/ProductCard.css";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import dhiramslogo from "../../assests/dhirams-png.webp";
import QuickCartDrawer from "../Common/QuickCartDrawer";
import { FaBolt } from "react-icons/fa";
import {
  DEFAULT_PRODUCT_IMAGE,
  cleanNumber,
  isProductOutOfStock,
  withDefaultProductImage,
} from "../../utils/productPresentation";
import { isUserLoggedIn, openAuthModal } from "../../utils/authModal";
import { getJsonStorageItem } from "../../utils/browser";
import { getBuyerToken } from "../../utils/authStorage";
import { addGuestCartItem } from "../../utils/guestCart";
import { showSuccess } from "../../utils/Toast";

export default function ProductCard({
  id,
  title,
  imageUrl,
  price,
  oldPrice,
  discount,
  tagText,
  onAdd,
  badge,
  rating,
  category_id,
  company_id,
  featured,
  status,
  stock_quantity,
  description,
  openDetailHandler,
  item,
  catname,
  onclickaddwishlist,
  is_wishlist,
  removewishlistrequest,
}) {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(false);
  const [quickDrawerOpen, setQuickDrawerOpen] = useState(false);

  useEffect(() => {
    if (is_wishlist !== undefined) {
      setIsFavorite(is_wishlist === 1);
      return;
    }

    const favorites = getJsonStorageItem("favorites", []);
    setIsFavorite(favorites.includes(id));
  }, [id, is_wishlist]);

  const fmt = (value) => {
    if (typeof value === "number") return value.toLocaleString("en-IN");
    if (!value && value !== 0) return "";
    return String(value);
  };

  const brandLabel = item?.brand_name || "";
  const sizeUnitLabel = [item?.size_dosage, item?.unit]
    .filter(Boolean)
    .join(" / ");
  const normalizedTagText =
    tagText === null || tagText === undefined ? "" : String(tagText).trim();
  const priceLikeTagText = normalizedTagText
    .replace(/\b(aed|dh|dhs|dirham)\b/gi, "")
    .replace(/[\s,.-]/g, "");
  const tagTextNumericValue = cleanNumber(normalizedTagText);
  const renderedPriceValues = [
    cleanNumber(price),
    Math.round(cleanNumber(price)),
    cleanNumber(oldPrice),
    Math.round(cleanNumber(oldPrice)),
  ].filter((value) => value > 0);
  const isDuplicatePriceTag =
    normalizedTagText &&
    priceLikeTagText !== "" &&
    /^\d+$/.test(priceLikeTagText) &&
    renderedPriceValues.includes(tagTextNumericValue);
  const displayTagText =
    normalizedTagText && !isDuplicatePriceTag ? normalizedTagText : "";

  const toggleFavorite = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (!isUserLoggedIn()) {
      openAuthModal();
      return;
    }

    if (!id) return;

    if (isFavorite) {
      removewishlistrequest?.(id);
    } else {
      onclickaddwishlist?.(item || { id, product_id: id, title });
    }

    setIsFavorite((prev) => !prev);
  };

  const openDetail = () => {
    if (openDetailHandler) {
      openDetailHandler();
      return;
    }

    const serializableProduct = {
      id,
      name: title,
      imageUrl,
      price,
      oldPrice,
      discount,
      tagText: displayTagText || null,
      badge,
      rating,
      category_id,
      company_id,
      featured,
      status,
      stock_quantity,
      description,
      attributes: item?.attributes,
      brand_name: item?.brand_name,
      category_name: item?.category_name,
      sub_category_name: item?.sub_category_name,
      size_dosage: item?.size_dosage,
      unit: item?.unit,
    };

    navigate(`/product/${id}`, {
      state: { product: serializableProduct, item, catname },
    });

    window.scrollTo(0, 0);
  };

  const handleAddToCart = (event) => {
    event.stopPropagation();
    event.preventDefault();

    const cartItem = {
      id,
      title,
      name: title,
      price,
      oldPrice,
      tagText: displayTagText || null,
      imageUrl,
      badge,
      rating,
      discount,
      item,
      attributes: item?.attributes || {},
    };

    if (!getBuyerToken()) {
      addGuestCartItem({
        product_id: id,
        quantity: 1,
        attributes: item?.attributes,
        offer_id: item?.active_offer?.offer_id ?? item?.offer_id,
      });
      showSuccess("Cart Added Successfully");
      setQuickDrawerOpen(true);
      return;
    }

    if (!onAdd) return;

    onAdd(cartItem);
    setQuickDrawerOpen(true);
  };

  const displayProduct = item || {
    id,
    title,
    name: title,
    price,
    oldPrice,
    discount,
    stock_quantity,
  };
  const isOutOfStock = isProductOutOfStock({
    ...displayProduct,
    status,
    stock_quantity,
  });

  return (
    <>
      <article
        className="pcard"
        onClick={openDetail}
        data-id={id}
        aria-label={title}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter") openDetail();
        }}
      >
        <div className="pcard-image-wrap">
          {discount ? <div className="pcard-badge">{discount}% OFF</div> : null}

          {isOutOfStock ? (
            <div className="pcard-badge-orange">Out of Stock</div>
          ) : badge ? (
            <div className="pcard-badge-orange">{badge}</div>
          ) : null}

          <button
            className="pcard-heart-float"
            onClick={toggleFavorite}
            aria-label={
              isFavorite ? "Remove from favorites" : "Add to favorites"
            }
            type="button"
          >
            {isFavorite ? (
              <FaHeart size={18} color="#d64545" />
            ) : (
              <FaRegHeart size={18} color="#475569" />
            )}
          </button>

          <img
            className="pcard-image"
            src={withDefaultProductImage(imageUrl)}
            alt={title}
            onError={(event) => {
              event.currentTarget.src = DEFAULT_PRODUCT_IMAGE;
            }}
          />
        </div>

        <div className="pcard-body">
          <div className="pcard-copy">
            <h3 className="pcard-title">{title}</h3>
            {brandLabel ? (
              <p className="pcard-meta">{brandLabel}</p>
            ) : null}
            {sizeUnitLabel ? (
              <p className="pcard-meta">
                {sizeUnitLabel}
              </p>
            ) : null}

            <div className="pcard-price-block">
              <div className="pcard-current">
                <img
                  src={dhiramslogo}
                  width={18}
                  height={18}
                  alt="Dirham"
                  className="pcard-currency"
                />
                <span>{fmt(price)}</span>
              </div>

              {oldPrice ? (
                <div className="pcard-price-meta">
                  <span className="pcard-old">{fmt(oldPrice)}</span>
                  {discount ? (
                    <span className="pcard-discount-text">{discount}% off</span>
                  ) : null}
                </div>
              ) : null}
            </div>

            {displayTagText ? (
              <p className="pcard-tagline line-clamp-2-custom">{displayTagText}</p>
            ) : null}
          </div>

          <div className="pcard-footer">
            {/* <div className="delivery-badge">
              <div
                style={{ width: "100%" }}
                className="delivery-promise is-placeholder"
                // aria-label={
                //   delivery.isPlaceholder
                //     ? "Delivery timing shown at checkout"
                //     : `Delivery in ${delivery.detail}`
                // }
              >
                <span className="delivery-promise__icon" aria-hidden="true">
                  <FaBolt />
                </span>
                <span className="delivery-promise__content">
                  <span className="delivery-promise__label">
                    Get it in 4 hour
                  </span>
                </span>
              </div>
            </div> */}

            {onAdd ? (
              <div className="pcard-actions">
                <button
                  type="button"
                  className="pcard-cta"
                  onClick={handleAddToCart}
                  disabled={isOutOfStock}
                  style={
                    isOutOfStock
                      ? { opacity: 0.7, cursor: "not-allowed" }
                      : undefined
                  }
                >
                  {isOutOfStock ? "Out of Stock" : "Add to Cart"}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </article>

      <QuickCartDrawer
        isOpen={quickDrawerOpen}
        onClose={() => setQuickDrawerOpen(false)}
        product={{
          ...displayProduct,
          id,
          title,
          name: title,
          price,
          oldPrice,
          discount,
          description,
          imageUrl,
          quantity: 1,
        }}
        priceLabel={`AED ${fmt(price)}`}
        imageUrl={imageUrl}
      />
    </>
  );
}

ProductCard.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string.isRequired,
  imageUrl: PropTypes.string,
  price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  oldPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tagText: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAdd: PropTypes.func,
  badge: PropTypes.string,
  rating: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  openDetailHandler: PropTypes.func,
  item: PropTypes.object,
  catname: PropTypes.string,
  onclickaddwishlist: PropTypes.func,
  removewishlistrequest: PropTypes.func,
  is_wishlist: PropTypes.oneOf([0, 1]),
  category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  company_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  featured: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  status: PropTypes.string,
  stock_quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  description: PropTypes.string,
};

ProductCard.defaultProps = {
  imageUrl: DEFAULT_PRODUCT_IMAGE,
  oldPrice: null,
  discount: null,
  tagText: null,
  onAdd: null,
  badge: null,
  rating: null,
  openDetailHandler: null,
  item: null,
  catname: "",
  onclickaddwishlist: () => {},
  removewishlistrequest: () => {},
  is_wishlist: undefined,
  category_id: null,
  company_id: null,
  featured: null,
  status: "",
  stock_quantity: null,
  description: "",
};
